import React, {
  MouseEvent,
} from 'react';
import {
  Row,
} from '@tanstack/react-table';
import {
  useNavigate,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  useMedia,
} from 'core/hooks';
import Name from 'pages/Dashboard/components/Name';
import StatusTag from 'core/components/StatusTag';
import WorkflowEncounterDisplay from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/WorkflowEncounterDisplay';
import FormCategoryCell from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/FormCategoryCell';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import workflowStatusMap from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/constants';
import {
  FormWorkflowRow,
} from 'pages/Dashboard/pages/Forms/pages/FormWorkflow/types';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useUserProfilePicturePath,
} from 'pages/Dashboard/hooks/media';

type PatientCellProps = {
  row: Row<FormWorkflowRow>;
};

function PatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const navigate = useNavigate();
  const link = getPatientChartPath(row.original.patient?.patientId);

  return (
    <Name
      link={{
        to: link,
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
          event.stopPropagation();
          if (!isNil(link)) {
            navigate(link);
          }
        },
      }}
      name={getDisplayName(row.original.patient?.formattedNames)}
      image={{ src: getMediaPathById(row.original.patientId ?? 0) ?? undefined }}
    />
  );
}

type AssignedClinicianCellProps = {
  row: Row<FormWorkflowRow>;
};

function AssignedClinicianCell({ row }: AssignedClinicianCellProps) {
  const user = row.original.assignedClinicianUser;
  const src = useUserProfilePicturePath(user?.id);

  return !user ? <span className="text-gray-400">—</span> : (
    <Name
      {...user?.formattedNames}
      image={{ src }}
    />
  );
}

type CreatedByCellProps = {
  row: Row<FormWorkflowRow>;
};

function CreatedByCell({ row }: CreatedByCellProps) {
  const user = row.original.initiatedByUser;
  const src = useUserProfilePicturePath(user?.id);

  return !user ? <span className="text-gray-400">—</span> : (
    <Name
      name={getDisplayName(user?.formattedNames)}
      image={{ src }}
      labelClassName="!overflow-visible !text-clip !whitespace-normal break-words"
    >
      <span className="text-xs text-gray-500">{row.original.formattedCreatedOn ?? DEFAULT_EMPTY_VALUE}</span>
    </Name>
  );
}

const columns: ColumnDef<FormWorkflowRow>[] = [{
  id: 'status',
  header: 'Status',
  size: 168,
  minSize: 168,
  cell: ({ row }) => (
    <StatusTag
      value={row.original.statusDisplayName ?? ''}
      statusMap={workflowStatusMap}
    />
  ),
}, {
  id: 'patient',
  header: 'Patient',
  size: 145,
  minSize: 135,
  cell: ({ row }) => <PatientCell row={row} />,
}, {
  id: 'assignedClinician',
  header: 'Assigned clinician',
  size: 185,
  minSize: 175,
  cell: ({ row }) => <AssignedClinicianCell row={row} />,
}, {
  id: 'encounter',
  header: 'Encounter',
  size: 280,
  minSize: 240,
  cell: ({ row }) => (
    <WorkflowEncounterDisplay encounter={row.original.encounter} />
  ),
}, {
  id: 'category',
  header: 'Form category',
  size: 130,
  minSize: 120,
  cell: ({ row }) => (
    <FormCategoryCell formCategories={row.original.formCategories} />
  ),
}, {
  id: 'createdBy',
  header: 'Created by',
  size: 240,
  minSize: 220,
  cell: ({ row }) => <CreatedByCell row={row} />,
}];

export default columns;
